'use client';

import type { GestureResponderEvent } from 'react-native';

import {
  Button,
  cn,
  Div,
  Nav,
  NavProps,
  VariantProps,
} from '@scatterlab/zeta-core';
import { createElement, ElementType, memo, MouseEvent } from 'react';

import TabBarItem, { TabBarItemProps } from './TabBarItem';
import { tabBarItem } from '../styles';

export type TabBarListItem<
  T extends string | number = string,
  ItemComponent extends ElementType = typeof Button,
> = Omit<TabBarItemProps<ItemComponent>, 'active' | 'size'> & {
  value: T;
  isNudge?: boolean;
  logLabel?: string;
};

export type TabBarProps<
  T extends string | number,
  ItemComponent extends ElementType = typeof Button,
> = Omit<NavProps, 'onChange'> & {
  value: T;
  onChange: (value: T) => void;
  items: readonly TabBarListItem<T, ItemComponent>[];
  size?: VariantProps<typeof tabBarItem>['size'];
  itemComponent?: ItemComponent;
};

const TabBar = <
  T extends string | number,
  ItemComponent extends ElementType = typeof Button,
>({
  value,
  onChange,
  className,
  children,
  items,
  size,
  itemComponent,
  ...props
}: TabBarProps<T, ItemComponent>) => (
  <Nav
    className={cn('flex flex-row', size !== 'full' && 'gap-[15px]', className)}
    {...props}
  >
    {items.map(
      ({ value: itemValue, onPress, isNudge, logLabel, ...itemProps }) => (
        <Div
          className={cn('relative', size === 'full' && 'flex-1')}
          key={itemValue}
        >
          {createElement(TabBarItem, {
            active: value === itemValue,
            key: itemValue,
            size,
            component: itemComponent,
            onPress: (e: GestureResponderEvent | MouseEvent) => {
              onPress?.(e);
              onChange(itemValue);
            },
            ...itemProps,
          })}
          {isNudge && (
            <Div className="absolute right-[-5px] top-[-2px] size-[5px] rounded-full bg-error-light" />
          )}
        </Div>
      ),
    )}
  </Nav>
);

export default memo(TabBar) as typeof TabBar;
